import React, { useState } from 'react';
import { Phone, Calculator, Star, Shield, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';
import QuoteModal from './QuoteModal';
import { useSettings } from '../hooks/useSettings';

const Hero = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { settings } = useSettings();

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(companyInfo.whatsappMessage);
    window.open(`https://wa.me/${settings.whatsappPhone}?text=${message}`, '_blank');
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Parallax */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mudancas-pro/artifacts/hyt3kel5_ChatGPT%20Image%2029%20de%20nov.%20de%202025%2C%2001_25_28.png)',
        }}
      >
        {/* Dark Overlay with Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#071028]/95 via-[#071028]/90 to-[#0077C8]/80"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-4xl mx-auto">
          {/* Centered Content */}
          <div className="text-white space-y-8 text-center">
            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-4 text-sm animate-fade-in-up delay-100">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full hover:bg-white/20 transition-all duration-300 hover:scale-110">
                <Clock className="w-4 h-4 text-[#1FA2FF]" />
                <span>20+ Anos</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full hover:bg-white/20 transition-all duration-300 hover:scale-110">
                <Star className="w-4 h-4 text-[#1FA2FF] fill-[#1FA2FF]" />
                <span>4.9 Avaliação</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full hover:bg-white/20 transition-all duration-300 hover:scale-110">
                <Shield className="w-4 h-4 text-[#1FA2FF]" />
                <span>Profissional Certificado</span>
              </div>
            </div>

            {/* Main Headline */}
            <div className="space-y-4 animate-fade-in-up delay-200">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Mudanças em Lisboa e Portugal:
                <span className="block text-[#1FA2FF] mt-2 animate-fade-in-up delay-300">
                  Sua Mudança Sem Stress e Sem Atrasos
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-200 leading-relaxed animate-fade-in-up delay-400">
                Líder em Transportes na região de Lisboa, Cascais, Sintra e Margem Sul
              </p>
            </div>

            {/* Subheadline */}
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto animate-fade-in-up delay-500">
              Orçamento rápido em <span className="text-[#1FA2FF] font-semibold animate-pulse">1 hora</span>.
              Equipa profissional que trata os seus bens como se fossem nossos.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-600">
              <Button
                onClick={handleWhatsAppClick}
                size="lg"
                className="bg-[#1FA2FF] hover:bg-[#0077C8] text-white font-bold text-lg px-8 py-6 rounded-lg transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:shadow-[#1FA2FF]/60 group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center">
                  <Phone className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform duration-300" />
                  Peça Orçamento no WhatsApp
                </span>
                {/* Button shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </Button>
              <Button
                onClick={() => setIsModalOpen(true)}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#071028] font-bold text-lg px-8 py-6 rounded-lg transition-all duration-300 hover:scale-110 hover:shadow-2xl group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center">
                  <Calculator className="w-5 h-5 mr-2 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300" />
                  Orçamento Rápido
                </span>
              </Button>
            </div>

            {/* Social Proof Text */}
            <p className="text-sm text-gray-400 italic animate-fade-in delay-700">
              ✓ Mais de 10000 mudanças realizadas em Lisboa e região
            </p>
          </div>

          {/* YouTube Video Section */}
          <div className="mt-16 animate-fade-in-up delay-700">
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl hover:shadow-[#1FA2FF]/30 transition-all duration-500 hover:scale-105 group">
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-[#1FA2FF] via-[#0077C8] to-[#1FA2FF] rounded-2xl blur opacity-30 group-hover:opacity-60 transition-opacity duration-500"></div>

              {/* YouTube iframe */}
              <iframe
                className="relative z-10 w-full h-full rounded-2xl"
                src={settings.videoUrl || "https://www.youtube.com/embed/VL1Nj7oHxbU"}
                title="Marcelo Carvalho Transportes - Vídeo Apresentação"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white/50 rounded-full animate-pulse"></div>
        </div>
      </div>

      {/* Quote Modal */}
      <QuoteModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
};

export default Hero;
